import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { Plus } from "lucide-react";
import { useApi, publicApi } from "../hooks/useApi";
import { useCart } from "../context/CartContext";
import { getBundleSavings } from "../lib/bundleSavings";

export default function Books() {
  const [tab, setTab] = useState("ebooks");
  const navigate = useNavigate();
  const api = useApi();
  const { isSignedIn } = useUser();
  const { addItem } = useCart();
  const { data: books, isLoading: loadingBooks } = useQuery({ queryKey: ["books"], queryFn: async () => (await publicApi.get("/books")).data });
  const { data: editions, isLoading: loadingEditions } = useQuery({ queryKey: ["audio-editions"], queryFn: async () => (await publicApi.get("/audio")).data });
  const { data: library } = useQuery({ queryKey: ["library-home"], queryFn: async () => (await api.get("/library")).data, enabled: !!isSignedIn });
  const ownedIds = new Set((library?.books || []).map((b) => b.id));

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16" data-testid="books-page">
      <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">Books</p>
      <h1 className="font-display text-display-md md:text-display-lg font-medium mb-8">The Digital Bookstore</h1>

      <div className="flex gap-6 border-b border-border mb-10">
        <button data-testid="books-tab-ebooks" onClick={() => setTab("ebooks")} className={`pb-3 text-sm uppercase tracking-wide ${tab === "ebooks" ? "border-b-2 border-foreground" : "text-textSecondary"}`}>
          eBooks
        </button>
        <button data-testid="books-tab-audiobooks" onClick={() => setTab("audiobooks")} className={`pb-3 text-sm uppercase tracking-wide ${tab === "audiobooks" ? "border-b-2 border-foreground" : "text-textSecondary"}`}>
          Audiobooks
        </button>
      </div>

      {tab === "ebooks" && (
        <div data-testid="books-ebooks-panel">
          {loadingBooks && <p className="text-white/70">Loading catalog…</p>}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {books?.map((b) => (
              <div key={b.id} data-testid={`book-card-${b.slug}`} className="group relative">
                <Link to={`/read/${b.id}`} className="block">
                  <img src={b.cover_image} alt={b.title} className="w-full aspect-square object-cover group-hover:opacity-80 transition-opacity duration-200" />
                  {b.category === "Bundle" && (
                    <span data-testid={`bundle-badge-${b.slug}`} className="absolute top-2 right-2 bg-black/85 border border-white/30 text-white text-[9px] uppercase tracking-wide px-1.5 py-0.5">Bundle</span>
                  )}
                  <p className="text-sm mt-3">{b.title}</p>
                  <p className="text-xs text-white/70 mt-1">${b.price.toFixed(2)}</p>
                  {getBundleSavings(b, books) != null && (
                    <p data-testid={`bundle-savings-${b.slug}`} className="text-[11px] text-brandAccent mt-0.5">You Save ${getBundleSavings(b, books).toFixed(0)}</p>
                  )}
                </Link>
                <div className="flex gap-2 mt-3">
                  {ownedIds.has(b.id) ? (
                    <button
                      data-testid={`open-reader-${b.slug}`}
                      onClick={() => navigate(`/reader/${b.id}`)}
                      className="flex-1 text-[10px] uppercase tracking-wide border border-white/40 py-2 hover:border-white transition-colors duration-200"
                    >
                      Owned — Open in Reader
                    </button>
                  ) : b.sale_blocked ? (
                    <span data-testid={`sale-blocked-${b.slug}`} className="flex-1 text-[10px] uppercase tracking-wide text-white/50 border border-white/15 py-2 text-center">
                      Temporarily Unavailable
                    </span>
                  ) : (
                    <>
                      <button
                        data-testid={`quick-buy-${b.slug}`}
                        onClick={() => navigate(`/checkout?buyNow=${b.id}`)}
                        className="flex-1 text-[10px] uppercase tracking-wide bg-white text-black py-2 hover:bg-white/80 transition-colors duration-200"
                      >
                        Quick Buy
                      </button>
                      <button
                        data-testid={`add-to-cart-${b.slug}`}
                        aria-label={`Add ${b.title} to cart`}
                        onClick={() => addItem(b.id, b.title)}
                        className="border border-white/30 px-2.5 hover:border-white transition-colors duration-200"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "audiobooks" && (
        <div data-testid="books-audiobooks-panel">
          <p className="text-white/60 max-w-2xl mb-8 text-sm">Original author explanations and behind-the-model analysis beyond the published narration.</p>
          {loadingEditions && <p className="text-white/70">Loading editions…</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {editions?.map((e) => (
              <Link key={e.id} to={`/audio/${e.id}`} data-testid={`audiobook-card-${e.slug}`} className="bg-surface border border-border p-5 hover:border-white/30 transition-colors duration-200">
                <img src={e.cover_image} alt="" className="w-full aspect-square object-cover mb-4" />
                <p className="text-sm">{e.title}</p>
                <p className="text-[11px] text-white/70 mt-2 uppercase tracking-wide">{e.purchase_enabled ? "Available" : "Coming Soon"}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
